import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../../api/axios";
import { useNameMode, NameModeToggle } from "../../../hooks/useNameMode";
import { useVoicePage } from "../../../hooks/useVoicePage";
import VoicePageMic from "../../../components/common/VoicePageMic";
import { matchPlayer } from "../../../utils/playerMatch";

export default function PlayingXI() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { renderName } = useNameMode();

  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState("");
  const [competitionId, setCompetitionId] = useState("");

  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [meta, setMeta] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [feedback, setFeedback] = useState(null);  // {ok, msg}
  // Track which teams' XIs the operator saved in THIS session. When the
  // first team's save lands, we use this to auto-switch the picker to
  // the OTHER team instead of navigating back to the match list — the
  // operator's "save Team A, save Team B, then go back" expectation
  // (previously they had to manually re-enter the page to pick Team B).
  const [savedTeamIds, setSavedTeamIds] = useState([]);

  /* =========================
     Load match
  ========================= */
  useEffect(() => {
    api
      .get(`/api/matches/matches/${id}/detail/`)
      .then((res) => {
        const data = res.data;
        setTeams([
          { id: data.team1_id, name: data.team1_name },
          { id: data.team2_id, name: data.team2_name },
        ]);
        setCompetitionId(data.competition || "");
      })
      .finally(() => setLoading(false));
  }, [id]);

  /* =========================
     Load squad / players

     Source priority:
       1. MatchSquad — the per-match squad the operator picked on /squad.
          This is the canonical pool; the XI MUST be 11 of these. New
          matches use this exclusively.
       2. Team.master_squad — fallback for legacy matches created before
          the MatchSquad concept (no /squad step yet performed).
       3. CompetitionSquad — older B2B fallback.
       4. my-players — last-resort so the operator is never stuck empty.
  ========================= */
  useEffect(() => {
    if (!teamId) return;
    let cancelled = false;

    const extract = (data) => data?.players || data || [];

    const loadFromMatchSquad = () =>
      api
        .get(`/api/matches/matches/${id}/match-squad/`)
        .then((res) => {
          const t1 = res.data?.team1, t2 = res.data?.team2;
          const block = t1?.team_id === teamId ? t1 : (t2?.team_id === teamId ? t2 : null);
          return block?.players || [];
        })
        .catch(() => []);

    const loadFromMasterSquad = () =>
      api
        .get(`/api/coredata/teams/${teamId}/squad/`)
        .then((res) => extract(res.data))
        .catch(() => []);

    const loadFromCompetitionSquad = () =>
      competitionId
        ? api
            .get(
              `/api/competitions/competition/${competitionId}/team/${teamId}/squad/`,
            )
            .then((res) => extract(res.data))
            .catch(() => [])
        : Promise.resolve([]);

    const loadMyPlayers = () =>
      api
        .get(`/api/coredata/my-players/`)
        .then((res) => extract(res.data))
        .catch(() => []);

    (async () => {
      let players = await loadFromMatchSquad();
      if (!players.length) players = await loadFromMasterSquad();
      if (!players.length) players = await loadFromCompetitionSquad();
      if (!players.length) players = await loadMyPlayers();
      if (cancelled) return;
      setAvailablePlayers(players);
      setSelectedPlayers([]);
      setMeta({});
    })();

    return () => {
      cancelled = true;
    };
  }, [id, teamId, competitionId]);

  /* =========================
     Add / Remove Player
  ========================= */
  const addPlayer = (player) => {
    if (selectedPlayers.some((p) => p.id === player.id)) return;
    // Cricket Law-of-the-Game: exactly 11 in the XI. Reject the 12th
    // pick visibly instead of silently truncating later.
    if (selectedPlayers.length >= 11) {
      setFeedback({
        ok: false,
        msg: "Playing XI can only have 11 players. Remove someone first or pick a different player.",
      });
      return;
    }
    setSelectedPlayers((prev) => [...prev, player]);
    setMeta((prev) => ({
      ...prev,
      [player.id]: {
        is_captain: false,
        is_wicket_keeper: false,
      },
    }));
    setFeedback(null);
  };

  const removePlayer = (playerId) => {
    setSelectedPlayers((prev) => prev.filter((p) => p.id !== playerId));
    setMeta((prev) => {
      const next = { ...prev };
      delete next[playerId];
      return next;
    });
  };

  /* =========================
     Meta updates
  ========================= */
  const toggleCaptain = (playerId) => {
    setMeta((prev) => {
      const next = {};
      Object.keys(prev).forEach((id) => {
        next[id] = {
          ...prev[id],
          is_captain: id === playerId,
        };
      });
      return next;
    });
  };

  const toggleWK = (playerId) => {
    setMeta((prev) => ({
      ...prev,
      [playerId]: {
        ...prev[playerId],
        is_wicket_keeper: !prev[playerId].is_wicket_keeper,
      },
    }));
  };

  /* =========================
     Voice — dictate names rapidfire + captain/keeper flags + save

     Founder ask 2026-05-26. Voice fills the form; the Save button
     stays manual so the operator can verify all 11 picks + captain +
     keeper before committing. Player names route through the same
     fuzzy matchPlayer() used by BBP voice (handles "Boomrah" ->
     Bumrah, "Kolly" -> Kohli, etc).
  ========================= */
  const xiParser = useCallback((raw) => {
    const t = raw.toLowerCase().replace(/[.,!?]+$/g, "").trim();
    if (!t || !availablePlayers || availablePlayers.length === 0) return null;

    // ── Role assignment FIRST so "captain Rohit" doesn't get parsed
    // as a player-add for "Captain Rohit".
    let m = t.match(/^(?:captain|skipper)\s+(.+)$/);
    if (m) {
      const p = matchPlayer(m[1], selectedPlayers);
      if (p) { toggleCaptain(p.id); return `captain → ${p.short_name || p.first_name || p.full_name}`; }
      return null;
    }
    m = t.match(/^(?:keeper|wicket keeper|wk|wicketkeeper)\s+(.+)$/);
    if (m) {
      const p = matchPlayer(m[1], selectedPlayers);
      if (p) { toggleWK(p.id); return `keeper → ${p.short_name || p.first_name || p.full_name}`; }
      return null;
    }

    // ── Explicit add/remove prefixes (helpful when a player surname
    // collides with a command verb).
    m = t.match(/^(?:add|pick|include)\s+(.+)$/);
    if (m) {
      const p = matchPlayer(m[1], availablePlayers);
      if (p) { addPlayer(p); return `+ ${p.short_name || p.first_name || p.full_name}`; }
      return null;
    }
    m = t.match(/^(?:remove|drop|delete)\s+(.+)$/);
    if (m) {
      const p = matchPlayer(m[1], selectedPlayers);
      if (p) { removePlayer(p.id); return `− ${p.short_name || p.first_name || p.full_name}`; }
      return null;
    }

    // ── Save shortcut.
    if (/^save( xi)?\s*$/.test(t) || /\bsave (the )?xi\b/.test(t)) {
      setTimeout(() => { document.getElementById("save-xi-btn")?.click(); }, 0);
      return "save";
    }

    // ── Batting order — "openers Rohit Kohli" puts them at #1 and #2.
    // "number three Iyer" / "no 3 Iyer" / "third Iyer" sets that
    // specific position. Founder ask 2026-05-26.
    m = t.match(/^openers?\s+(.+)$/);
    if (m) {
      const names = m[1].split(/\s*(?:,|\band\b|\s{2,})\s*/).filter(Boolean);
      if (names.length >= 2) {
        const p1 = matchPlayer(names[0], selectedPlayers);
        const p2 = matchPlayer(names[1], selectedPlayers);
        if (p1 && p2 && p1.id !== p2.id) {
          setSelectedPlayers((prev) => {
            const remaining = prev.filter((p) => p.id !== p1.id && p.id !== p2.id);
            return [p1, p2, ...remaining];
          });
          return `openers → ${(p1.short_name || p1.last_name)} & ${(p2.short_name || p2.last_name)}`;
        }
      }
      return null;
    }
    const ordWord = {
      one: 1, two: 2, three: 3, four: 4, five: 5,
      six: 6, seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11,
      first: 1, second: 2, third: 3, fourth: 4, fifth: 5,
      sixth: 6, seventh: 7, eighth: 8, ninth: 9, tenth: 10, eleventh: 11,
    };
    m = t.match(/^(?:number|no\.?|position|bat at|at)\s+(\d{1,2}|one|two|three|four|five|six|seven|eight|nine|ten|eleven|first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|eleventh)\s+(.+)$/);
    if (m) {
      const posRaw = m[1].toLowerCase();
      const pos = parseInt(posRaw, 10) || ordWord[posRaw] || null;
      if (pos && pos >= 1 && pos <= 11) {
        const p = matchPlayer(m[2], selectedPlayers);
        if (p) {
          setSelectedPlayers((prev) => {
            const without = prev.filter((x) => x.id !== p.id);
            // Insert at pos-1 (zero-indexed), clamp to length.
            const idx = Math.min(pos - 1, without.length);
            return [...without.slice(0, idx), p, ...without.slice(idx)];
          });
          return `#${pos} → ${p.short_name || p.last_name}`;
        }
      }
    }

    // ── Comma-separated dictation: "kohli, rohit, bumrah, shami"
    // Operators rattle off names while looking at the team-sheet.
    // Split on punctuation OR " and " OR " , " and try to match each
    // token as a player. Reports the count picked.
    const tokens = t.split(/\s*(?:,|;|\band\b|\s{2,})\s*/).map((s) => s.trim()).filter(Boolean);
    const added = [];
    const missed = [];
    for (const tok of tokens) {
      if (!tok || tok.length < 2) continue;
      const p = matchPlayer(tok, availablePlayers);
      if (!p) { missed.push(tok); continue; }
      // skip if already picked
      if (selectedPlayers.some((s) => s.id === p.id)) continue;
      addPlayer(p);
      added.push(p.short_name || p.first_name || p.full_name);
    }
    if (added.length === 0 && missed.length === tokens.length) return null;
    let label = added.length ? `+ ${added.join(", ")}` : "";
    if (missed.length) label += ` (no match: ${missed.slice(0, 3).join(", ")}${missed.length > 3 ? "…" : ""})`;
    return label || null;
  }, [availablePlayers, selectedPlayers]);

  const voice = useVoicePage({ parser: xiParser });

  /* =========================
     Validation
  ========================= */
  const captainCount = Object.values(meta).filter((m) => m?.is_captain).length;
  const wkCount = Object.values(meta).filter((m) => m?.is_wicket_keeper).length;
  // EXACTLY 11 players, EXACTLY 1 captain, EXACTLY 1 wicket-keeper.
  // The backend enforces the same constraint; matching here just prevents
  // the round-trip + 400.
  const canSave =
    selectedPlayers.length === 11 && captainCount === 1 && wkCount === 1;

  /* =========================
     Save XI
  ========================= */
  const saveXI = async () => {
    if (!canSave) {
      // Surface the missing constraint inline so the disabled button is
      // never silent.
      const reasons = [];
      if (selectedPlayers.length !== 11) reasons.push(`pick exactly 11 players (have ${selectedPlayers.length})`);
      if (captainCount !== 1) reasons.push(`mark exactly 1 captain (have ${captainCount})`);
      if (wkCount !== 1) reasons.push(`mark exactly 1 wicket-keeper (have ${wkCount})`);
      setFeedback({ ok: false, msg: `Can't save yet — ${reasons.join("; ")}.` });
      return;
    }
    setFeedback(null);
    setSaving(true);
    try {
      await api.put(`/api/matches/matches/${id}/playing-xi/`, {
        team_id: teamId,
        players: selectedPlayers.map((p, idx) => ({
          player_id: p.id,
          batting_position: idx + 1,
          is_captain: !!meta[p.id]?.is_captain,
          is_wicket_keeper: !!meta[p.id]?.is_wicket_keeper,
        })),
      });
      // Mark this team done; pick what to do next based on whether the
      // OTHER team still needs an XI.
      const nextSaved = savedTeamIds.includes(teamId)
        ? savedTeamIds
        : [...savedTeamIds, teamId];
      setSavedTeamIds(nextSaved);
      const otherTeam = teams.find((t) => !nextSaved.includes(t.id));
      if (otherTeam) {
        // Auto-switch to the other team. Reset the picker state so the
        // operator's previous XI selections don't bleed across teams.
        setFeedback({
          ok: true,
          msg: `Saved. Now pick the XI for ${otherTeam.name}.`,
        });
        setSelectedPlayers([]);
        setMeta({});
        setSearch("");
        setTeamId(otherTeam.id);
      } else {
        // Both teams done — back to match list.
        setFeedback({
          ok: true,
          msg: "Both teams' XIs saved. Redirecting to match list…",
        });
        setTimeout(() => navigate(`/cricket/matches`), 1200);
      }
    } catch (err) {
      // DRF returns {"detail": "..."} OR {"<field>": ["error", ...]} OR
      // a top-level non_field_errors list. Show whatever's there instead
      // of falling through to "Request failed with status code 400".
      const data = err?.response?.data;
      let msg = "Failed to save XI";
      if (data?.detail) {
        msg = data.detail;
      } else if (data?.players) {
        msg = Array.isArray(data.players) ? data.players.join("; ") : String(data.players);
      } else if (data?.non_field_errors) {
        msg = Array.isArray(data.non_field_errors) ? data.non_field_errors.join("; ") : String(data.non_field_errors);
      } else if (data && typeof data === "object") {
        msg = Object.entries(data)
          .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
          .join("; ");
      } else if (err?.message) {
        msg = err.message;
      }
      setFeedback({ ok: false, msg });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-main flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-button-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading match details...</p>
        </div>
      </div>
    );
  }

  const filteredAvailable = availablePlayers.filter((p) =>
    `${p.first_name || ""} ${p.last_name || ""}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );

  return (
    <div className="min-h-screen bg-bg-main">
      {/* Header — stacks vertically on phone/tablet so Save + voice + name
          toggle stay reachable. The previous single-row layout pushed the
          Save button off-screen on phones (see 2026-06-01 mobile audit). */}
      <div className="bg-bg-primary border-b border-bg-secondary shadow-lg sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-3 min-w-0">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg bg-bg-secondary hover:bg-bg-main transition-colors shrink-0"
              >
                <svg
                  className="w-5 h-5 text-text-secondary"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
              </button>
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl font-bold text-text-primary">
                  Match Squad Selection
                </h1>
                <p className="text-xs sm:text-sm text-text-secondary break-words">
                  Select at least 11 players with 1 captain. You can add extra players for substitutes or impact-player usage.
                </p>
              </div>
              <VoicePageMic
                voice={voice}
                helpTitle="Playing XI — voice commands"
                examples={[
                  "Kohli, Rohit, Bumrah, Shami",
                  "add Pant",
                  "captain Rohit",
                  "keeper Pant",
                  "remove Shami",
                  "save xi",
                ]}
                helpBody={
                  <>
                    Dictate names comma-separated to pick them in one
                    shot. Prefix with <em>captain</em> / <em>keeper</em>
                    to set the role. Save stays manual — verify your
                    11 + roles before clicking.
                  </>
                }
              />
            </div>

            <div className="flex items-center gap-2 flex-wrap lg:gap-3 lg:flex-nowrap">
              {/* Operator-controlled "first name vs display name" toggle.
                  Choice persists across pages via useNameMode (localStorage). */}
              <NameModeToggle />
              {selectedPlayers.length >= 11 && captainCount !== 1 && (
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <svg
                    className="w-4 h-4 text-amber-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <span className="text-sm text-amber-400">
                    Select 1 captain
                  </span>
                </div>
              )}
              {selectedPlayers.length < 11 && (
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <svg
                    className="w-4 h-4 text-blue-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M12 18a6 6 0 100-12 6 6 0 000 12z"
                    />
                  </svg>
                  <span className="text-sm text-blue-400">
                    Select at least 11 players
                  </span>
                </div>
              )}

              {feedback && (
                <div className={`text-sm px-3 py-1.5 rounded mr-3 ${
                  feedback.ok
                    ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"
                    : "bg-rose-500/15 text-rose-300 border border-rose-500/40"
                }`}>
                  {feedback.ok ? "✓" : "⚠"} {feedback.msg}
                </div>
              )}
              <button
                id="save-xi-btn"
                onClick={saveXI}
                disabled={!canSave || saving}
                className="ml-auto lg:ml-0 px-5 sm:px-6 py-2 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shrink-0"
                style={{
                  backgroundColor: canSave ? "#6B1991" : undefined,
                  color: canSave ? "#FFFFFF" : undefined,
                }}
              >
                {saving ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Save Match Squad
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-3 sm:p-6">
        {/* Team Selector */}
        <div className="bg-bg-primary rounded-xl p-4 sm:p-6 mb-4 sm:mb-6 border border-bg-secondary shadow-lg">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0 border border-blue-500/30">
              <svg
                className="w-6 h-6 text-blue-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <label className="block text-sm text-text-secondary mb-2">
                Select Team <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <select
                  value={teamId}
                  onChange={(e) => setTeamId(e.target.value)}
                  className="w-full p-3 px-4 pr-10 rounded-lg bg-bg-main text-text-primary border border-bg-secondary focus:border-button-primary outline-none transition-colors appearance-none cursor-pointer"
                >
                  <option value="">-- Choose a team --</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
                <svg
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-secondary pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
              <p className="text-xs text-text-secondary mt-2">
                Select a team to view available players
              </p>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        {teamId && (
          <div className="grid md:grid-cols-4 gap-4 mb-6">
            <div className="bg-bg-primary rounded-lg p-4 border border-bg-secondary">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-text-secondary mb-1">Available</p>
                  <p className="text-2xl font-bold text-text-primary">
                    {availablePlayers.length}
                  </p>
                </div>
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-blue-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-bg-primary rounded-lg p-4 border border-bg-secondary">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-text-secondary mb-1">Selected</p>
                  <p className="text-2xl font-bold text-text-primary">
                    {selectedPlayers.length}
                  </p>
                </div>
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    selectedPlayers.length >= 11
                      ? "bg-emerald-500/20"
                      : "bg-purple-500/20"
                  }`}
                >
                  <svg
                    className={`w-5 h-5 ${selectedPlayers.length >= 11 ? "text-emerald-400" : "text-purple-400"}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-bg-primary rounded-lg p-4 border border-bg-secondary">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-text-secondary mb-1">Captain</p>
                  <p className="text-2xl font-bold text-text-primary">
                    {captainCount}/1
                  </p>
                </div>
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    captainCount === 1 ? "bg-emerald-500/20" : "bg-amber-500/20"
                  }`}
                >
                  <svg
                    className={`w-5 h-5 ${captainCount === 1 ? "text-emerald-400" : "text-amber-400"}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-bg-primary rounded-lg p-4 border border-bg-secondary">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-text-secondary mb-1">
                    Wicket Keeper
                  </p>
                  <p className="text-2xl font-bold text-text-primary">
                    {wkCount}
                  </p>
                </div>
                <div className="w-10 h-10 bg-teal-500/20 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-teal-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Selection Grid */}
        {teamId && (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Available Players */}
            <div className="bg-bg-primary rounded-xl border border-bg-secondary shadow-lg overflow-hidden">
              <div className="bg-bg-secondary px-6 py-4 border-b border-bg-main">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
                    <svg
                      className="w-5 h-5 text-blue-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6h16M4 10h16M4 14h16M4 18h16"
                      />
                    </svg>
                    Available
                  </h3>
                  <span className="px-3 py-1 bg-bg-main rounded-full text-sm text-text-secondary">
                    {filteredAvailable.length}
                  </span>
                </div>

                <div className="relative">
                  <svg
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-secondary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <input
                    placeholder="Search players..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 rounded-lg bg-bg-main text-text-primary border border-bg-main focus:border-button-primary outline-none transition-colors placeholder:text-text-secondary"
                  />
                </div>

                {/* Quick-fill from squad / available pool. Adds up to 11
                    players starting with the current order — useful when
                    the operator confirmed the squad on the previous
                    screen and just wants the same 11 selected. Skips
                    duplicates so re-clicking is safe. */}
                {selectedPlayers.length < 11 && availablePlayers.length > 0 && (
                  <button
                    onClick={() => {
                      const taken = new Set(selectedPlayers.map((p) => p.id));
                      const need = 11 - selectedPlayers.length;
                      const toAdd = availablePlayers.filter((p) => !taken.has(p.id)).slice(0, need);
                      if (!toAdd.length) return;
                      setSelectedPlayers((prev) => [...prev, ...toAdd]);
                      setMeta((prev) => {
                        const next = { ...prev };
                        for (const p of toAdd) {
                          next[p.id] = { is_captain: false, is_wicket_keeper: false };
                        }
                        return next;
                      });
                      setFeedback({
                        ok: true,
                        msg: `Auto-filled ${toAdd.length} player${toAdd.length === 1 ? "" : "s"} from squad. Mark captain + WK below.`,
                      });
                    }}
                    className="mt-3 w-full text-[11px] uppercase tracking-wider px-3 py-2 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 font-semibold"
                    title="Picks the first 11 players from the squad into the Playing XI"
                  >
                    ⚡ Use first 11 from squad
                  </button>
                )}
              </div>

              <div className="p-4 max-h-[600px] overflow-y-auto">
                {filteredAvailable.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-text-secondary text-sm">
                      No players found
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredAvailable.map((p) => {
                      const isSelected = selectedPlayers.some(
                        (sp) => sp.id === p.id,
                      );

                      return (
                        <div
                          key={p.id}
                          onClick={() => !isSelected && addPlayer(p)}
                          className={`p-3 rounded-lg border transition-all ${
                            isSelected
                              ? "bg-emerald-500/10 border-emerald-500/30 opacity-50 cursor-not-allowed"
                              : "bg-bg-secondary border-bg-main hover:border-button-primary/50 hover:bg-bg-main cursor-pointer"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold shrink-0 ${
                                isSelected
                                  ? "bg-emerald-500/20 text-emerald-400"
                                  : "bg-gradient-to-br from-blue-500/20 to-purple-500/20 text-blue-400"
                              }`}
                            >
                              {isSelected ? "✓" : (renderName(p)?.[0] || "?")}
                            </div>
                            <span className="text-text-primary flex-1 min-w-0 truncate">
                              {renderName(p)}
                            </span>
                            {p.ci_player_id && (
                              <span
                                className="text-[10px] font-mono text-text-secondary shrink-0"
                                title={`Cricinfo ID ${p.ci_player_id}`}
                              >
                                #{p.ci_player_id}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Selected Match Squad */}
            <div className="lg:col-span-2 bg-bg-primary rounded-xl border border-bg-secondary shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 px-6 py-4 border-b border-purple-500/20">
                <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
                  <svg
                    className="w-5 h-5 text-purple-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Match Squad ({selectedPlayers.length} selected)
                </h3>
              </div>

              <div className="p-4 max-h-[600px] overflow-y-auto">
                {selectedPlayers.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg
                        className="w-8 h-8 text-text-secondary"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                        />
                      </svg>
                    </div>
                    <p className="text-text-secondary text-sm">
                      No players selected
                    </p>
                    <p className="text-text-secondary text-xs mt-1">
                      Select at least 11 players from the available list. You can select more than 11 if needed.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {selectedPlayers.map((p, idx) => (
                      <div
                        key={p.id}
                        className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-purple-500/20 rounded-full flex items-center justify-center text-sm font-bold text-purple-400">
                              {idx + 1}
                            </div>
                            <div>
                              <p className="text-text-primary font-medium flex items-center gap-2 flex-wrap">
                                <span>{renderName(p)}</span>
                                {p.ci_player_id && (
                                  <span
                                    className="text-[10px] font-mono text-text-secondary"
                                    title={`Cricinfo ID ${p.ci_player_id}`}
                                  >
                                    #{p.ci_player_id}
                                  </span>
                                )}
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                {meta[p.id]?.is_captain && (
                                  <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded border border-amber-500/30">
                                    Captain
                                  </span>
                                )}
                                {meta[p.id]?.is_wicket_keeper && (
                                  <span className="px-2 py-0.5 bg-teal-500/20 text-teal-400 text-xs rounded border border-teal-500/30">
                                    WK
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={meta[p.id]?.is_captain || false}
                                onChange={() => toggleCaptain(p.id)}
                                className="w-4 h-4 accent-amber-500 cursor-pointer"
                              />
                              <span className="text-sm text-text-secondary">
                                C
                              </span>
                            </label>

                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={meta[p.id]?.is_wicket_keeper || false}
                                onChange={() => toggleWK(p.id)}
                                className="w-4 h-4 accent-teal-500 cursor-pointer"
                              />
                              <span className="text-sm text-text-secondary">
                                WK
                              </span>
                            </label>

                            <button
                              onClick={() => removePlayer(p.id)}
                              className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* No Team Selected State */}
        {!teamId && (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-bg-primary rounded-full flex items-center justify-center mx-auto mb-4 border border-bg-secondary">
              <svg
                className="w-10 h-10 text-text-secondary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-text-primary mb-2">
              Select a Team
            </h3>
            <p className="text-text-secondary">
              Choose a team from the dropdown above to select the match squad
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
